import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import apiService from '../services/api';
import CopyableId from '../components/common/CopyableId';
import PaginationControls from '../components/common/PaginationControls';

const SavedItemsPage = () => {
  const [savedItems, setSavedItems] = useState([]);
  const [programDetails, setProgramDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5); // Dynamic, will be calculated
  const [totalItems, setTotalItems] = useState(0);
  const [selectedPrograms, setSelectedPrograms] = useState([]); // State for selected programs for comparison
  const containerRef = useRef(null);
  const { user } = useAuth();
  const { t, navigateToComparePrograms } = useAppContext();
  const navigate = useNavigate();
  
  // Get userId from authenticated user
  const userId = user?.user_id;

  // Calculate items per page based on available height
  const calculateItemsPerPage = useCallback(() => {
    if (!containerRef.current) return 5;
    
    const containerHeight = containerRef.current.clientHeight;
    // Account for: title (~56px), table header (~45px), pagination (~60px), padding (~32px)
    const fixedHeight = 56 + 45 + 60 + 32;
    const availableHeight = containerHeight - fixedHeight;
    // Each row is approximately 100px (h-24 = 96px for image + py-2 = 8px padding)
    const rowHeight = 100;
    // Subtract 1 to fine-tune page size and prevent overflow
    const calculatedItems = Math.max(1, Math.floor(availableHeight / rowHeight) - 1);
    return calculatedItems;
  }, []);

  // Update items per page on mount and window resize
  useEffect(() => {
    const updateItemsPerPage = () => {
      const newItemsPerPage = calculateItemsPerPage();
      setItemsPerPage(prev => {
        if (prev !== newItemsPerPage) {
          return newItemsPerPage;
        }
        return prev;
      });
    };

    // Initial calculation after a brief delay to ensure DOM is ready
    const timeoutId = setTimeout(updateItemsPerPage, 100);
    
    // Add resize listener
    window.addEventListener('resize', updateItemsPerPage);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateItemsPerPage);
    };
  }, [calculateItemsPerPage]);

  const fetchSavedItems = async (page, limit) => {
    if (!userId) {
      setError(t('savedItems.loginRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getSavedItems(userId, page, limit);
      const items = response.items;
      const total = response.total;

      setSavedItems(items);
      setTotalItems(total);
      setCurrentPage(response.page);
      
      // Fetch program details for each saved item
      const detailsPromises = items.map(async (item) => {
        try {
          const overview = await apiService.getOverview(item.value);
          return { tmsId: item.value, details: overview };
        } catch (err) {
          console.error(`Failed to fetch details for ${item.value}:`, err);
          return { tmsId: item.value, details: null };
        }
      });
      
      const detailsResults = await Promise.all(detailsPromises);
      
      // Convert array of results to an object keyed by tmsId
      const detailsMap = detailsResults.reduce((acc, result) => {
        acc[result.tmsId] = result.details;
        return acc;
      }, {});
      
      setProgramDetails(detailsMap);
    } catch (err) {
      console.error('Failed to fetch saved items:', err);
      setError(t('savedItems.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedItems(currentPage, itemsPerPage);
  }, [userId, currentPage, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteItem = async (tmsId) => {
    if (!userId || !tmsId) return;

    try {
      await apiService.deleteSavedItem(userId, tmsId);
      // Remove the deleted item from the state
      setSavedItems(savedItems.filter(item => item.value !== tmsId));
    } catch (err) {
      console.error('Failed to delete saved item:', err);
      setError(t('savedItems.deleteError'));
    }
  };

  const handleViewProgram = (tmsId) => {
    if (tmsId) {
      navigate(`/program/${tmsId}`);
    }
  };

  return (
    <div ref={containerRef} className="p-4 md:p-6 flex flex-col h-full overflow-hidden">
      <h1 className="text-2xl font-semibold mb-4 flex-shrink-0">{t('savedItems.title')}</h1>

      {isLoading && (
        <div className="flex justify-center items-center p-8">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-3 text-gray-600">{t('savedItems.loading')}</span>
        </div>
      )}

      {error && (
        <div className="text-center text-red-600 bg-red-100 p-4 rounded border border-red-300">
          {error}
        </div>
      )}

      {/* Compare Button - shown when 2+ items selected */}
      {selectedPrograms.length >= 2 && (
        <div className="mb-4">
          <button
            className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            onClick={() => navigateToComparePrograms(selectedPrograms.join(','))}
          >
            {t('searchResults.compare', { count: selectedPrograms.length })}
          </button>
        </div>
      )}

      {!isLoading && !error && savedItems.length === 0 && (
        <div className="text-center text-gray-500 p-4">
          {t('savedItems.noItems')}
        </div>
      )}

      {!isLoading && !error && savedItems.length > 0 && (
        <div className="flex-1 overflow-x-auto overflow-y-auto bg-white shadow rounded-lg min-h-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10"> {/* Added sticky top-0 z-10 for header visibility during scroll */}
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={selectedPrograms.length > 0 && selectedPrograms.length === savedItems.length}
                    onChange={() => {
                      if (selectedPrograms.length === savedItems.length) {
                        setSelectedPrograms([]);
                      } else {
                        setSelectedPrograms(savedItems.map(item => item.value));
                      }
                    }}
                    title={selectedPrograms.length > 0 ? t('searchResults.tooltips.deselectAll') : t('savedItems.tooltips.selectAll')}
                  />
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.image')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.title')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.tmsId')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.genres')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.type')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.subType')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('searchResults.columns.year')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('savedItems.columns.savedDate')}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('savedItems.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {savedItems.map((item) => {
                const details = programDetails[item.value] || {};
                const genres = details.genres ? 
                  details.genres
                    .sort((a, b) => a.genreOrd - b.genreOrd)
                    .map(g => g.genre)
                    .join(', ') : 
                  'N/A';
                
                // Extract year from mvinfo if available
                let year = 'N/A';
                if (details.mvinfo) {
                  const yearInfo = details.mvinfo.find(info => info.key === 'yearOfRelease');
                  if (yearInfo) {
                    year = yearInfo.value;
                  }
                }
                
                // Get image URL if available
                const imageUrl = details.image_url || null;
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {/* Checkbox Cell */}
                    <td className="px-4 py-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={selectedPrograms.includes(item.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPrograms(prev => [...prev, item.value]);
                          } else {
                            setSelectedPrograms(prev => prev.filter(id => id !== item.value));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-left">
                      {imageUrl ? (
                        <img
                          className="w-16 h-24 object-contain cursor-pointer"
                          src={imageUrl}
                          alt={details.title || 'Program image'}
                          onClick={() => handleViewProgram(item.value)}
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/160x90?text=No+Image'; }}
                        />
                      ) : (
                        <div
                          className="w-16 h-24 bg-gray-200 flex items-center justify-center text-xs text-gray-400 cursor-pointer"
                          onClick={() => handleViewProgram(item.value)}
                        >
                          {t('searchResults.noImage')}
                        </div>
                      )}
                    </td>
                    <td 
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:underline cursor-pointer text-left"
                      onClick={() => handleViewProgram(item.value)}
                    >
                      {details.title || t('savedItems.unknownTitle')}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      <div className="flex"> {/* Removed items-center */}
                        <CopyableId value={item.value} />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      {genres}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      {details.progType || 'N/A'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      {details.subType || 'N/A'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      {year}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-left">
                      {new Date(item.created * 1000).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-left">
                      <button
                        onClick={() => handleViewProgram(item.value)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        {t('savedItems.actions.view')}
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.value)}
                        className="text-red-600 hover:text-red-900"
                      >
                        {t('savedItems.actions.delete')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !error && totalItems > 0 && (
        <div className="flex-shrink-0 mt-2 bg-white rounded-lg shadow py-1 px-2">
          <PaginationControls
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SavedItemsPage;
